import { TransportStates } from '@arpeggi/shared'
import React, { useEffect, useRef } from 'react'
import { useTreeState } from '../../../stores/Root'

const useTrackTable = () => {
  const { studio } = useTreeState()
  // for track table scrolling
  const [hasScrolledSincePlaying, setHasScrolledSincePlaying] = React.useState(false)
  const [tracktableScrollLeft, setTracktableScrollLeft] = React.useState(0)
  const [playheadCoords, setPlayheadCoords] = React.useState(null)
  const [isClickingTrackTable, setIsClickingTrackTable] = React.useState(false)

  const trackStems = useRef<HTMLDivElement>(null)
  const song = studio.song

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    const currentXScroll = target.scrollLeft
    if (
      studio.transportState === TransportStates.PLAYING &&
      !hasScrolledSincePlaying &&
      currentXScroll !== tracktableScrollLeft &&
      isClickingTrackTable
    )
      setHasScrolledSincePlaying(true)
    setTracktableScrollLeft(target.scrollLeft)
  }

  const scrollSync = (selector: string) => {
    let active = null
    document.querySelectorAll(selector).forEach(function (div) {
      div.addEventListener('mouseenter', function (mouseEvt) {
        active = mouseEvt.target
      })

      div.addEventListener('scroll', function (scrollEvt) {
        if (scrollEvt.target !== active) return

        document.querySelectorAll(selector).forEach(function (target) {
          if (active === target) return
          target.scrollTop = active.scrollTop
        })
      })
    })
  }

  const onClickTrackStemsContainer = () => {
    setIsClickingTrackTable(true)
    document.addEventListener('mouseup', () => setIsClickingTrackTable(false), {
      once: true
    })
  }

  // sync scrolling of track headers and stems containers
  useEffect(() => {
    scrollSync('.scrollsync')
  }, [])

  // scroll to playhead if it's offscreen
  useEffect(() => {
    if (!playheadCoords || hasScrolledSincePlaying) return
    const containerCoords = trackStems.current.getBoundingClientRect()
    const playheadIsOffscreenRight = playheadCoords.x > containerCoords.right
    const playheadIsOffscreenLeft = playheadCoords.x < containerCoords.left - 10
    if (trackStems) {
      if (playheadIsOffscreenLeft) {
        trackStems.current.scrollTo({
          left: playheadCoords.x_offset
        })
      } else if (playheadIsOffscreenRight) {
        trackStems.current.scrollBy({
          left: 100
        })
      }
    }
  }, [studio.playheadVisiblePositionInTicks])

  // scroll to beginning of song if someone stops it while it's playing
  useEffect(() => {
    if (studio.transportState !== TransportStates.PLAYING) setHasScrolledSincePlaying(false)
    if (!playheadCoords) return
    if (studio.transportState !== TransportStates.PLAYING && studio.playhead === 0) {
      trackStems.current.scrollTo({
        left: 0
      })
    }
  }, [studio.transportState])

  useEffect(() => {
    if (song.stems.length > 0) studio.setSelectedStemIndex(0)
  }, [song.stems.length > 0])

  return {
    trackStems,
    onScroll,
    setPlayheadCoords,
    onClickTrackStemsContainer
  }
}

export default useTrackTable
